import React, { useEffect, useState } from 'react';
import { 
    Modal, 
    Box, 
    Typography, 
    IconButton, 
    Grid, 
    Paper, 
    Chip, 
    Divider, 
    Tooltip,
    Button,
    Collapse,
    useTheme
} from '@mui/material';
import { X, Calendar, Gauge, Fuel, Zap, Car, Settings, Palette, Tag, ShieldCheck, Leaf, Info, Snowflake, Users, Hash, ListChecks, Type, CheckSquare, ChevronDown, ChevronUp, Sparkles, FileText } from 'lucide-react';
import Carousel from 'react-material-ui-carousel';
import { formatPrice } from '../../../utils/utils';

const modalStyle = (darkMode) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95%', sm: '80%', md: '70%', lg: '60%' },
  maxHeight: '90vh',
  bgcolor: darkMode ? '#2C2C2C' : 'background.paper',
  border: 'none',
  boxShadow: 24,
  borderRadius: '12px',
  p: 0, // Padding handled internally
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const DetailItem = ({ icon, label, value, isChip = false }) => {
  if (!value) return null; // Don't render if value is empty or null
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
      {icon}
      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{label}:</Typography>
      {isChip ? (
         <Chip label={value} size="small" sx={{ ml: 'auto' }}/>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>{value}</Typography>
      )}
    </Box>
  );
};

// AccessorySection component to display accessories or optionals
const AccessorySection = ({ title, items = [], icon, darkMode }) => {
  const [expanded, setExpanded] = useState(false);
  
  // If no items, don't render the section
  if (!items || items.length === 0) return null;
  
  // Determine how many items to show in collapsed state
  const previewCount = 8;
  const hasMore = items.length > previewCount;
  
  // Get the items to display based on expanded state
  const displayedItems = expanded ? items : items.slice(0, previewCount);
  
  return (
    <Box sx={{ mt: 3, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ 
          fontWeight: 'bold', 
          display: 'flex', 
          alignItems: 'center',
          gap: 1
        }}>
          {icon}
          {title}
          <Chip 
            label={items.length} 
            size="small" 
            color="primary" 
            sx={{ ml: 1, fontSize: '0.7rem', height: '20px' }} 
          />
        </Typography>
        
        {hasMore && (
          <Button
            size="small"
            variant="text"
            color="primary"
            onClick={() => setExpanded(!expanded)}
            endIcon={expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            sx={{ textTransform: 'none' }}
          >
            {expanded ? 'Riduci' : 'Mostra tutto'}
          </Button>
        )}
      </Box>
      
      <Divider sx={{ mb: 2 }} />
      
      <Grid container spacing={1}>
        {displayedItems.map((item, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Box sx={{ 
              py: 1, 
              px: 1, 
              borderBottom: '1px solid',
              borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
              '&:last-child': { borderBottom: 'none' }
            }}>
              <Typography variant="body2" color="text.secondary">
                {item}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
      
      {!expanded && hasMore && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic', textAlign: 'center' }}>
          + altri {items.length - previewCount} elementi
        </Typography>
      )}
    </Box>
  );
};

const CarDetailModal = ({ car, open, onClose, darkMode }) => {
  // Helper function to extract values regardless of property casing
  const getCarProperty = (propertyNames) => {
    if (!car) return null;
    
    // If propertyNames is a single string, convert to array
    const names = Array.isArray(propertyNames) ? propertyNames : [propertyNames];
    
    // Try each property name until we find a value
    for (const name of names) {
      // Try exact name
      if (car[name] !== undefined) return car[name];
      
      // Try lowercase version
      const lowerName = name.toLowerCase();
      if (car[lowerName] !== undefined) return car[lowerName];
      
      // Try camelCase version
      const camelName = lowerName.charAt(0) + lowerName.slice(1).replace(/[-_](.)/g, (_, c) => c.toUpperCase());
      if (car[camelName] !== undefined) return car[camelName];
    }
    
    return null;
  };
  
  // Add debug logging when car changes
  useEffect(() => {
    if (car) {
      //console.log("CarDetailModal received car data:", car);
      // Log all possible image-related properties
      //console.log("Car image sources:", {
      //  imagesUrl: car.imagesUrl,
      //  urlmainimage: car.urlmainimage,
      //  urlMainImage: car.urlMainImage
      //});
      //console.log("Price information:", {
      //  salePrice: car.salePrice,
      //  saleprice: car.saleprice,
      //  retailprice: car.retailprice,
      //  RetailPrice: car.RetailPrice
      //});
    }
  }, [car]);

  // Handler for the Generate SEO button
  const handleGenerateSeo = () => {
    if (!car) return;
    
    const vehicleId = getCarProperty(['vehicleid', 'id']);
    if (!vehicleId) {
      console.error("Cannot generate SEO: Missing vehicle ID");
      return;
    }
    
    // Create a message event that the internal assistant will process
    const message = `Generate seo per vehicleid: ${vehicleId}`;
    
    // Send the message to the chat
    // This uses window.postMessage to communicate with the parent component
    window.postMessage({
      type: 'internalAssistantMessage',
      message: message
    }, '*');
    
    // Close the modal after sending the message
    onClose();
  };

  if (!car) return null;

  // Helper to format cylinder info
  const formatCylinders = (cap, num) => {
    if (!cap && !num) return null;
    let parts = [];
    if (cap) parts.push(`${cap}cc`);
    if (num && num !== '00') parts.push(`${num} cil.`);
    return parts.join(' / ');
  }

  // Get image URLs from multiple possible sources
  let carImages = [];
  
  // First check if imagesUrl array exists and has items
  const imagesUrl = getCarProperty('imagesUrl');
  if (imagesUrl && Array.isArray(imagesUrl) && imagesUrl.length > 0) {
    carImages = imagesUrl;
  } 
  // If no imagesUrl array but there's a urlmainimage or urlMainImage, use that
  else {
    const mainImage = getCarProperty(['urlmainimage', 'urlMainImage', 'mainImage']);
    if (mainImage) {
      carImages = [mainImage];
    }
  }
  
  // Try to handle sale price with different naming conventions
  const formattedPrice = formatPrice(car.salePrice || car.saleprice || car.retailprice || car.RetailPrice);
  
  // Filter out any invalid URL values
  const validImages = carImages.filter(url => url && typeof url === 'string');
  //console.log("Valid images for carousel:", validImages);

  // Get accessories and optional features
  const standardAccessories = getCarProperty(['accessories', 'standardAccessories', 'standardFeatures']) || [];
  const optionalAccessories = getCarProperty(['optionals', 'optionalAccessories', 'optionalFeatures']) || [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="car-detail-modal-title"
      aria-describedby="car-detail-modal-description"
      closeAfterTransition
    >
      <Box sx={modalStyle(darkMode)}>
        {/* Header */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Typography id="car-detail-modal-title" variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
            {getCarProperty('brand')} {getCarProperty('model') || ''}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<FileText size={16} />}
              onClick={handleGenerateSeo}
              sx={{
                textTransform: 'none',
                borderRadius: 1.5,
                fontWeight: 500,
              }}
            >
              Genera SEO
            </Button>
            <IconButton onClick={onClose} size="small">
              <X />
            </IconButton>
          </Box>
        </Box>

        {/* Content Area (Scrollable) */}
        <Box sx={{ overflowY: 'auto', p: 3 }}>
          <Grid container spacing={3}>
            {/* Image Carousel */}
            <Grid item xs={12} md={6}>
              {validImages.length > 0 ? (
                <Carousel
                  autoPlay={false}
                  animation="slide"
                  navButtonsAlwaysVisible={validImages.length > 1}
                  indicators={validImages.length > 1}
                  sx={{ borderRadius: '8px', overflow: 'hidden' }}
                >
                  {validImages.map((imageUrl, index) => (
                    <Paper key={index} elevation={0} sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: darkMode ? '#424242' : '#f5f5f5' }}>
                      <img 
                        src={imageUrl} 
                        alt={`${getCarProperty('brand')} ${getCarProperty('model') || ''} - Image ${index + 1}`} 
                        style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                        loading="lazy"
                        onError={(e) => {
                            console.error(`Failed to load image at index ${index}:`, imageUrl);
                            e.target.onerror = null;
                            
                            // Try to fix common URL issues before falling back to placeholder
                            let fixedUrl = imageUrl;
                            // If URL doesn't have http/https, try adding it
                            if (!imageUrl.startsWith('http')) {
                                fixedUrl = `https://${imageUrl}`;
                                //console.log(`Trying fixed URL: ${fixedUrl}`);
                                e.target.src = fixedUrl;
                            } else {
                                // If that didn't work, use placeholder
                                e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23bdbdbd' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='3' width='18' height='18' rx='2' ry='2'%3E%3C/rect%3E%3Ccircle cx='9' cy='9' r='2'%3E%3C/circle%3E%3Cpath d='m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21'%3E%3C/path%3E%3C/svg%3E";
                                e.target.style.padding = '40px';
                            }
                        }}
                      />
                    </Paper>
                  ))}
                </Carousel>
              ) : (
                <Paper elevation={0} sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: darkMode ? '#424242' : '#f5f5f5', borderRadius: '8px' }}>
                  <Car size={60} color="#bdbdbd" />
                </Paper>
              )}
            </Grid>

            {/* Details */}
            <Grid item xs={12} md={6}>
              <Typography variant="body1" sx={{ mb: 1.5 }}>{getCarProperty('description') || 'Descrizione non disponibile'}</Typography>
              <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold', mb: 2 }}>
                {formattedPrice}
              </Typography>
              
              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5 }}>Specifiche Principali</Typography>
              <Grid container spacing={0}> {/* Reduced spacing */} 
                 <Grid item xs={12}><DetailItem icon={<Tag size={16}/>} label="Versione" value={getCarProperty(['version', 'versiondescription'])} /></Grid>
                 <Grid item xs={12} sm={6}><DetailItem icon={<Calendar size={16}/>} label="Anno Reg." value={getCarProperty('registrationYear')} /></Grid>
                 <Grid item xs={12} sm={6}><DetailItem icon={<Gauge size={16}/>} label="Chilometri" value={`${getCarProperty('km')?.toLocaleString() || '0'} km`} /></Grid>
                 <Grid item xs={12} sm={6}><DetailItem icon={<Fuel size={16}/>} label="Carburante" value={getCarProperty(['fuel', 'fueltype'])} /></Grid>
                 <Grid item xs={12} sm={6}>
                   {(getCarProperty(['kwPower', 'kwpower']) || getCarProperty('hp')) ? (
                     <DetailItem 
                       icon={<Zap size={16}/>} 
                       label="Potenza" 
                       value={(() => {
                         const kw = getCarProperty(['kwPower', 'kwpower']);
                         const hp = getCarProperty('hp');
                         let powerValue = '';
                         if (kw) powerValue += `${kw} kW`;
                         if (kw && hp) powerValue += ' / ';
                         if (hp) powerValue += `${hp} CV`;
                         return powerValue || 'N/D'; // Show N/D if somehow both are missing despite check
                       })()}
                     />
                   ) : null /* Don't render if both kW and HP are missing */}
                 </Grid>
                 <Grid item xs={12} sm={6}><DetailItem icon={<Settings size={16}/>} label="Cambio" value={getCarProperty(['transmission', 'gearbox'])} /></Grid>
                 <Grid item xs={12} sm={6}><DetailItem icon={<Palette size={16}/>} label="Colore" value={getCarProperty(['colour', 'color'])} /></Grid>
                 <Grid item xs={12} sm={6}><DetailItem icon={<Car size={16}/>} label="Carrozzeria" value={getCarProperty(['bodyStyle', 'bodytype'])} /></Grid>
                 <Grid item xs={12} sm={6}><DetailItem icon={<Info size={16}/>} label="Trazione" value={getCarProperty(['traction', 'drivetrain'])} /></Grid>
                 <Grid item xs={12} sm={6}><DetailItem icon={<Snowflake size={16}/>} label="Classe Emis." value={getCarProperty('emissionClass')} /></Grid>
                 <Grid item xs={12} sm={6}>
                   <DetailItem 
                     icon={<ListChecks size={16}/>} 
                     label="Cilindrata/Num." 
                     value={formatCylinders(
                       getCarProperty(['cilynderCapacity', 'cylinderCapacity']),
                       getCarProperty(['cilynders', 'cylinderCount'])
                     )} 
                   />
                 </Grid>
                 <Grid item xs={12} sm={6}><DetailItem icon={<Type size={16}/>} label="Tipo Veicolo" value={getCarProperty(['vehicleType', 'vehicletype', 'usagetype'])} /></Grid>
                 <Grid item xs={12} sm={6}><DetailItem icon={<CheckSquare size={16}/>} label="Stato" value={getCarProperty(['statusDescription', 'statusdescription'])} isChip={true} /></Grid>
                 <Grid item xs={12}><DetailItem icon={<Hash size={16}/>} label="Telaio (VIN)" value={getCarProperty(['chassisID', 'chassisid', 'vin'])} /></Grid>
                 
                 {getCarProperty('hybridDescription') && 
                    <Grid item xs={12} sm={6}>
                      <DetailItem icon={<Leaf size={16}/>} label="Ibrido" value={getCarProperty('hybridDescription')} />
                    </Grid>}
                 
                 {getCarProperty(['warranty', 'warrantyperiod']) && 
                    <Grid item xs={12} sm={6}>
                      <DetailItem icon={<ShieldCheck size={16}/>} label="Garanzia" value={getCarProperty(['warranty', 'warrantyperiod'])} />
                    </Grid>}
              </Grid>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                {getCarProperty('usagetype') && (
                   <Chip label={getCarProperty('usagetype')} size="small" sx={{ bgcolor: darkMode ? 'rgba(255,255,255,0.1)': 'rgba(0,0,0,0.06)' }} />
                )}
                 {getCarProperty('isYoungDriverFit') === true && (
                   <Chip icon={<Users size={16} />} label="Neopatentati OK" size="small" color="success" variant="outlined" />
                 )}
                 {getCarProperty('isYoungDriverFit') === false && (
                   <Chip icon={<Users size={16} />} label="Non per Neopatentati" size="small" color="warning" variant="outlined" />
                 )} 
              </Box>
            </Grid>
          </Grid>
          
          {/* Accessories and Optional Features Section */}
          {(standardAccessories.length > 0 || optionalAccessories.length > 0) && (
            <>
              <Divider sx={{ my: 4 }} />
              
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
                Dotazioni e Optional
              </Typography>
              
              <AccessorySection 
                title="Dotazioni di Serie" 
                items={standardAccessories} 
                icon={<Car size={18} />} 
                darkMode={darkMode} 
              />
              
              <AccessorySection 
                title="Optional Inclusi" 
                items={optionalAccessories} 
                icon={<Sparkles size={18} />} 
                darkMode={darkMode} 
              />
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default CarDetailModal; 